import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, MessageSquareQuote, ShieldCheck, ChevronRight, X } from "lucide-react";

const SLIDES = [
  {
    eyebrow: "STEP 1",
    icon: Camera,
    headline: "Snap Your Car Issue",
    body: "Take a photo or video of the problem. No mechanic jargon needed — just show what's wrong.",
    accent: "#FF3B30",
  },
  {
    eyebrow: "STEP 2",
    icon: MessageSquareQuote,
    headline: "Get Private Quotes",
    body: "Verified local shops compete for your business. Compare prices without revealing your identity.",
    accent: "#FFB800",
  },
  {
    eyebrow: "STEP 3",
    icon: ShieldCheck,
    headline: "Choose & Fix",
    body: "Pick the best quote, chat with the shop, and get your car fixed. Simple as that.",
    accent: "#34C759",
  },
];

export default function Welcome() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  const finish = () => {
    try { localStorage.setItem("hasSeenOnboarding_v1", "1"); } catch {}
    navigate("/", { replace: true });
  };

  const next = () => (idx < SLIDES.length - 1 ? setIdx(idx + 1) : finish());
  const skip = () => finish();

  const onTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx < -40 && idx < SLIDES.length - 1) setIdx(idx + 1);
    if (dx > 40 && idx > 0) setIdx(idx - 1);
    setTouchStartX(null);
  };

  // Pre-load keyboard arrow nav for web users
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft" && idx > 0) setIdx(idx - 1);
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const slide = SLIDES[idx];
  const Icon = slide.icon;

  return (
    <div
      className="min-h-screen bg-[#09090B] text-white flex flex-col"
      data-testid="welcome-screen"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Skip button */}
      <div className="flex items-center justify-between px-6 pt-[max(env(safe-area-inset-top),1.25rem)]">
        <div className="font-display text-sm tracking-widest text-zinc-500">SHIFTQUOTE</div>
        <button
          type="button"
          onClick={skip}
          className="text-zinc-400 hover:text-white text-sm flex items-center gap-1"
          data-testid="welcome-skip-btn"
        >
          Skip <X className="w-4 h-4" />
        </button>
      </div>

      {/* Hero panel — accent glow + icon */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <div
          className="relative w-44 h-44 sm:w-56 sm:h-56 flex items-center justify-center mb-10"
          aria-hidden
        >
          <div
            className="absolute inset-0 rounded-full blur-3xl opacity-25"
            style={{ background: slide.accent }}
          />
          <div
            className="absolute inset-6 rounded-full border-2"
            style={{ borderColor: slide.accent }}
          />
          <Icon className="relative w-20 h-20 sm:w-24 sm:h-24" style={{ color: slide.accent }} />
        </div>

        <div className="label-eyebrow mb-3" style={{ color: slide.accent }}>
          {slide.eyebrow}
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl mb-5 leading-[1.05]"
          data-testid="welcome-headline"
        >
          {slide.headline}
        </h1>
        <p className="text-base text-zinc-400 max-w-md leading-relaxed">{slide.body}</p>
      </div>

      {/* Dots + CTA */}
      <div className="px-8 pb-[max(env(safe-area-inset-bottom),2rem)] flex flex-col items-center gap-6">
        <div className="flex gap-2" data-testid="welcome-dots">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === idx ? "1.75rem" : "0.5rem",
                background: i === idx ? slide.accent : "#3F3F46",
              }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={next}
          className="w-full max-w-sm py-4 font-display text-lg tracking-wide flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
          style={{ background: slide.accent, color: "#09090B" }}
          data-testid="welcome-next-btn"
        >
          {idx < SLIDES.length - 1 ? "Continue" : "Get Started"}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
